/** The app's runtime settings (from the Worker) and the firm's colours (from GET /firm). */
import type { FirmInfo, PublicConfig } from "./types";

let current: PublicConfig = { stripeJsUrl: "https://js.stripe.com/v3/", mapboxToken: null };

/** Loads /app-config.json. Call once when the app starts. */
export async function loadAppConfig(): Promise<PublicConfig> {
  const response = await fetch("/app-config.json");
  if (!response.ok) throw new Error(`app-config.json: ${response.status}`);
  current = (await response.json()) as PublicConfig;
  return current;
}

/** The settings loaded at start-up. */
export const appConfig = (): PublicConfig => current;

/**
 * Whether the app is showing its dark look: brand.json's appearance (set on <html> when the
 * app is built), or the phone's dark mode when it follows the phone.
 */
export function isDark(): boolean {
  const look = document.documentElement.dataset.appearance;
  if (look === "dark") return true;
  if (look === "light") return false;
  return window.matchMedia("(prefers-color-scheme: dark)").matches;
}

/** Applies the firm's colours (light and dark) to the whole app. */
export function applyColours(colours: FirmInfo["colours"]) {
  const root = document.documentElement;
  const dark = isDark();
  const palette = (dark ? colours.dark : colours.light) as Record<string, string>;
  if (palette.primary) root.style.setProperty("--primary", palette.primary);
  if (palette.onPrimary) root.style.setProperty("--on-primary", palette.onPrimary);
  // Optional: the firm's colour for small things on the background (icons, the selected tab),
  // for a colour too light or dark to read there as it is.
  if (palette.accent) root.style.setProperty("--accent", palette.accent);
  document
    .querySelector('meta[name="theme-color"]')
    ?.setAttribute("content", palette.background ?? (dark ? "#111111" : "#ffffff"));
}
